// Settings → Message Templates tab.
//
// Retailer-authored pre-written messages (label + body), optionally tagged to a
// CRM pipeline stage so Kit can suggest the right one. Picked from a "Templates"
// button in the messaging composer.
//
// Platform-only contact model: a template is just the retailer's own message
// TEXT — it carries NO customer contact details and offers no phone/email merge
// fields. Sending always goes through the masked /portal/customer-message path.
//
// Backend (migration 0060, all RLS-scoped to the signed-in retailer):
//   GET    /api/portal/message-templates
//   POST   /api/portal/message-templates      { label, body, pipelineStage? }
//   PUT    /api/portal/message-templates/:id   { label?, body?, pipelineStage? }
//   DELETE /api/portal/message-templates/:id

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";

import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./message-templates.css";

interface Template {
  id: string;
  label: string;
  body: string;
  channel: string;
  pipeline_stage: string | null;
}

// CRM pipeline stages a template can be tagged to (mirrors the 5-stage pipeline).
const STAGES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "Any stage" },
  { value: "lead", label: "Lead" },
  { value: "qualified", label: "Qualified" },
  { value: "quoted", label: "Quoted" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function stageLabel(v: string | null): string {
  return STAGES.find((s) => s.value === (v ?? ""))?.label ?? "Any stage";
}

const messageTemplatesTab: SettingsTab = {
  id: "message-templates",
  label: "Message Templates",
  icon: iconMarkup("chat", { size: "sm" }),
  mount(container: HTMLElement, ctx: PageContext) {
    const ac = new AbortController();
    let disposed = false;
    let templates: Template[] = [];

    container.innerHTML = `
      <section class="set-mt-root" aria-labelledby="set-mt-h">
        <h2 id="set-mt-h" class="set-section-title">Message templates</h2>
        <p class="set-help">
          Pre-written messages you can drop into a conversation in one click. Tag a
          template to a pipeline stage and Kit will suggest it at the right moment.
          Templates contain your own text only — never customer contact details.
        </p>
        <div class="set-mt-list" data-role="list"></div>
        <div class="set-mt-editor" data-role="editor"></div>
      </section>
    `;

    const listEl = container.querySelector<HTMLElement>('[data-role="list"]')!;
    const editorEl = container.querySelector<HTMLElement>('[data-role="editor"]')!;

    function renderEditor(editing: Template | null): void {
      editorEl.innerHTML = `
        <h3 class="set-mt-editor-title">${editing ? "Edit template" : "New template"}</h3>
        <div class="set-field">
          <label for="set-mt-label">Label</label>
          <input id="set-mt-label" class="set-input" type="text" maxlength="120"
            placeholder="e.g. Quote sent follow-up" data-role="f-label" value="${editing ? esc(editing.label) : ""}" />
        </div>
        <div class="set-field">
          <label for="set-mt-stage">Pipeline stage</label>
          <select id="set-mt-stage" class="set-input" data-role="f-stage">
            ${STAGES.map((s) => `<option value="${s.value}" ${editing && (editing.pipeline_stage ?? "") === s.value ? "selected" : ""}>${s.label}</option>`).join("")}
          </select>
        </div>
        <div class="set-field">
          <label for="set-mt-body">Message text</label>
          <textarea id="set-mt-body" class="set-input set-mt-body" rows="4" maxlength="8000"
            placeholder="Hi, just checking you received the quote we sent…" data-role="f-body">${editing ? esc(editing.body) : ""}</textarea>
        </div>
        <p class="set-mt-error" data-role="f-error" hidden></p>
        <div class="set-mt-editor-actions">
          ${editing ? `<button type="button" class="set-btn set-btn-secondary" data-role="cancel">Cancel</button>` : ""}
          <button type="button" class="set-btn" data-role="save">${editing ? "Save changes" : "Add template"}</button>
        </div>
      `;
      const labelEl = editorEl.querySelector<HTMLInputElement>('[data-role="f-label"]')!;
      const stageEl = editorEl.querySelector<HTMLSelectElement>('[data-role="f-stage"]')!;
      const bodyEl = editorEl.querySelector<HTMLTextAreaElement>('[data-role="f-body"]')!;
      const errEl = editorEl.querySelector<HTMLElement>('[data-role="f-error"]')!;
      const saveBtn = editorEl.querySelector<HTMLButtonElement>('[data-role="save"]')!;
      const cancelBtn = editorEl.querySelector<HTMLButtonElement>('[data-role="cancel"]');

      const showErr = (m: string | null): void => { errEl.hidden = !m; errEl.textContent = m ?? ""; };

      cancelBtn?.addEventListener("click", () => renderEditor(null));
      saveBtn.addEventListener("click", () => {
        const label = labelEl.value.trim();
        const body = bodyEl.value.trim();
        if (!label) { showErr("Please give the template a label."); return; }
        if (!body) { showErr("Please enter the message text."); return; }
        showErr(null);
        saveBtn.disabled = true;
        void (async () => {
          try {
            const payload = { label, body, pipelineStage: stageEl.value || null };
            if (editing) {
              await ctx.api(`/portal/message-templates/${encodeURIComponent(editing.id)}`, {
                method: "PUT", body: JSON.stringify(payload), signal: ac.signal,
              });
              ctx.showToast("Template updated", "success");
            } else {
              await ctx.api("/portal/message-templates", {
                method: "POST", body: JSON.stringify(payload), signal: ac.signal,
              });
              ctx.showToast("Template added", "success");
            }
            renderEditor(null);
            await load();
          } catch (err) {
            saveBtn.disabled = false;
            showErr(err instanceof ApiError ? "Couldn't save — please try again." : "Couldn't save the template.");
          }
        })();
      });
    }

    function renderList(): void {
      if (templates.length === 0) {
        listEl.innerHTML = `<div class="set-mt-empty">No templates yet. Create your first below.</div>`;
        return;
      }
      listEl.innerHTML = templates.map((t) => `
        <div class="set-mt-card" data-id="${esc(t.id)}">
          <div class="set-mt-card-head">
            <span class="set-mt-card-label">${esc(t.label)}</span>
            <span class="set-mt-card-stage">${esc(stageLabel(t.pipeline_stage))}</span>
          </div>
          <p class="set-mt-card-body">${esc(t.body)}</p>
          <div class="set-mt-card-actions">
            <button type="button" class="set-btn set-btn-secondary set-btn-sm" data-role="edit">Edit</button>
            <button type="button" class="set-btn set-btn-danger set-btn-sm" data-role="delete">Delete</button>
          </div>
        </div>
      `).join("");
    }

    async function load(): Promise<void> {
      try {
        const r = await ctx.api<{ ok: boolean; templates: Template[] }>(
          "/portal/message-templates", { signal: ac.signal });
        if (disposed) return;
        templates = Array.isArray(r.templates) ? r.templates : [];
        renderList();
      } catch (err) {
        if (disposed) return;
        listEl.innerHTML = `<div class="set-mt-empty">Couldn't load templates.</div>`;
      }
    }

    listEl.addEventListener("click", (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const card = target.closest<HTMLElement>(".set-mt-card");
      if (!card) return;
      const id = card.dataset["id"];
      const t = templates.find((x) => x.id === id);
      if (!t) return;
      if (target.closest('[data-role="edit"]')) {
        renderEditor(t);
        editorEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } else if (target.closest('[data-role="delete"]')) {
        void (async () => {
          try {
            await ctx.api(`/portal/message-templates/${encodeURIComponent(t.id)}`, { method: "DELETE", signal: ac.signal });
            ctx.showToast("Template deleted", "success");
            await load();
          } catch { ctx.showToast("Couldn't delete the template.", "error"); }
        })();
      }
    });

    renderEditor(null);
    void load();

    return () => { disposed = true; ac.abort(); };
  },
};

export default messageTemplatesTab;
